<template>
  <v-dialog v-model="clientStore.showClientModal" max-width="1200px" scrollable content-class="fixed-dialog-height">
    <v-card class="d-flex flex-column h-100">
      <v-card-title class="bg-primary text-white d-flex justify-space-between align-center flex-shrink-0">
        <span>Client Management</span>
        <v-btn
          icon
          variant="text"
          color="white"
          @click="clientStore.closeClientModal"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 flex-grow-1 d-flex flex-column overflow-hidden">
        <!-- Main View: Client List -->
        <div v-if="!clientStore.selectedClient" class="client-list-view">
          <!-- Search Bar -->
          <div class="pa-4 bg-grey-lighten-4 flex-shrink-0">
            <v-text-field
              v-model="searchInput"
              label="Search by name, email, or phone"
              prepend-inner-icon="mdi-magnify"
              clear-icon="mdi-close-circle-outline"
              persistent-clear
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @update:model-value="debounceSearch"
            ></v-text-field>
          </div>

          <!-- Client List -->
          <div class="client-list pa-4">
            <v-progress-linear
              v-if="clientStore.loading"
              indeterminate
              color="primary"
            ></v-progress-linear>

            <div v-if="!clientStore.loading && clientStore.clients.length === 0" class="text-center pa-8">
              <v-icon size="64" color="grey">mdi-account-search</v-icon>
              <div class="text-h6 mt-4">No clients found</div>
            </div>

            <v-list v-if="!clientStore.loading && clientStore.clients.length > 0">
              <v-list-item
                v-for="client in clientStore.clients"
                :key="client.id"
                class="client-item mb-2"
                @click="viewClientDetails(client.id)"
              >
                <template #prepend>
                  <v-avatar color="primary" size="40">
                    <span class="text-white">{{ getInitials(client) }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-bold">
                  {{ client.lastname }}, {{ client.firstname }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <div>{{ client.phone }}</div>
                  <div>{{ client.email }}</div>
                </v-list-item-subtitle>
                <template #append>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click.stop="viewClientDetails(client.id)"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <!-- Pagination -->
          <div v-if="clientStore.lastPage > 1" class="pagination-bar pa-4 bg-grey-lighten-4 d-flex justify-center align-center flex-shrink-0">
            <v-btn
              icon
              variant="text"
              :disabled="clientStore.currentPage === 1"
              @click="clientStore.prevPage()"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-4">
              Page {{ clientStore.currentPage }} of {{ clientStore.lastPage }}
              ({{ clientStore.total }} clients)
            </span>
            <v-btn
              icon
              variant="text"
              :disabled="clientStore.currentPage === clientStore.lastPage"
              @click="clientStore.nextPage()"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Detail View: Client Details and Appointments -->
        <div v-if="clientStore.selectedClient" class="client-detail-view">
          <!-- Back Button and Action Buttons -->
          <div class="pa-4 bg-grey-lighten-4 d-flex justify-space-between align-center">
            <v-btn
              variant="text"
              prepend-icon="mdi-arrow-left"
              @click="backToList"
            >
              Back to List
            </v-btn>
            <div class="d-flex gap-2">
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="createNewAppointment"
              >
                New Appointment
              </v-btn>
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="showAddNoteForm"
              >
                Add Note
              </v-btn>
            </div>
          </div>

          <!-- Tabs -->
          <v-tabs v-model="activeTab" bg-color="grey-lighten-2">
            <v-tab value="details">
              <v-icon start>mdi-account</v-icon>
              Client Details
            </v-tab>
            <v-tab value="appointments">
              <v-icon start>mdi-calendar</v-icon>
              Appointment History
            </v-tab>
            <v-tab value="notes">
              <v-icon start>mdi-note-text</v-icon>
              Notes
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab" class="tab-content-container">
            <v-window-item value="details" class="tab-scrollable">
              <ClientDetailsTab :client="clientStore.selectedClient" />
            </v-window-item>

            <v-window-item value="appointments" class="tab-scrollable">
              <AppointmentHistoryTab
                :appointments="clientStore.clientAppointments"
                :loading="clientStore.loading"
                @create-appointment="createNewAppointment"
                @clone-appointment="cloneAppointment"
              />
            </v-window-item>

            <v-window-item value="notes" class="tab-scrollable">
              <NotesTab
                ref="notesTabRef"
                :client="clientStore.selectedClient"
                :notes="clientStore.clientNotes"
                :loading="clientStore.loading"
                @notes-updated="handleNotesUpdated"
              />
            </v-window-item>
          </v-window>

          <!-- Pagination for Appointments and Notes -->
          <div v-if="showPagination" class="pagination-bar pa-4 bg-grey-lighten-4 d-flex justify-center align-center flex-shrink-0">
            <v-btn
              icon
              variant="text"
              :disabled="currentPaginationPage === 1"
              @click="handlePrevPage"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-4">
              Page {{ currentPaginationPage }} of {{ lastPaginationPage }}
              ({{ totalPaginationItems }} {{ paginationLabel }})
            </span>
            <v-btn
              icon
              variant="text"
              :disabled="currentPaginationPage === lastPaginationPage"
              @click="handleNextPage"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useClientStore } from "../../stores/clientStore";
import { useEditStore } from "../../stores/EditFormStore";
import { useEventStore } from "../../stores/EventStore";
import ClientDetailsTab from "./tabs/ClientDetailsTab.vue";
import AppointmentHistoryTab from "./tabs/AppointmentHistoryTab.vue";
import NotesTab from "./tabs/NotesTab.vue";

const clientStore = useClientStore();
const editStore = useEditStore();
const eventStore = useEventStore();

const searchInput = ref("");
const activeTab = ref("details");
const notesTabRef = ref(null);
let searchTimeout = null;

// Computed properties for pagination
const showPagination = computed(() => {
  if (activeTab.value === 'appointments') {
    return clientStore.appointmentsLastPage > 1;
  } else if (activeTab.value === 'notes') {
    return clientStore.notesLastPage > 1;
  }
  return false;
});

const currentPaginationPage = computed(() => {
  if (activeTab.value === 'appointments') {
    return clientStore.appointmentsCurrentPage;
  } else if (activeTab.value === 'notes') {
    return clientStore.notesCurrentPage;
  }
  return 1;
});

const lastPaginationPage = computed(() => {
  if (activeTab.value === 'appointments') {
    return clientStore.appointmentsLastPage;
  } else if (activeTab.value === 'notes') {
    return clientStore.notesLastPage;
  }
  return 1;
});

const totalPaginationItems = computed(() => {
  if (activeTab.value === 'appointments') {
    return clientStore.appointmentsTotal;
  } else if (activeTab.value === 'notes') {
    return clientStore.notesTotal;
  }
  return 0;
});

const paginationLabel = computed(() => {
  if (activeTab.value === 'appointments') {
    return 'appointments';
  } else if (activeTab.value === 'notes') {
    return 'notes';
  }
  return 'items';
});

const handleNextPage = () => {
  if (activeTab.value === 'appointments') {
    clientStore.nextAppointmentsPage();
  } else if (activeTab.value === 'notes') {
    clientStore.nextNotesPage();
  }
};

const handlePrevPage = () => {
  if (activeTab.value === 'appointments') {
    clientStore.prevAppointmentsPage();
  } else if (activeTab.value === 'notes') {
    clientStore.prevNotesPage();
  }
};

const getInitials = (client) => {
  return (client.firstname?.charAt(0) || "") + (client.lastname?.charAt(0) || "");
};

const debounceSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    clientStore.setSearchQuery(searchInput.value);
  }, 500);
};


const viewClientDetails = async (clientId) => {
  await clientStore.loadClientDetails(clientId);
  activeTab.value = "details";
};

const backToList = () => {
  clientStore.selectedClient = null;
  clientStore.clientAppointments = [];
  clientStore.clientNotes = [];
  activeTab.value = "details";
};

const handleNotesUpdated = async () => {
  if (clientStore.selectedClient) {
    await clientStore.loadClientNotes(clientStore.selectedClient.id);
  }
};

const createNewAppointment = () => {
  console.log('createNewAppointment called, selectedClient:', clientStore.selectedClient);

  if (!clientStore.selectedClient) {
    console.error('No client selected for new appointment');
    return;
  }

  // Capture customer data BEFORE closing modal
  const customerData = {
    id: clientStore.selectedClient.id,
    firstname: clientStore.selectedClient.firstname,
    lastname: clientStore.selectedClient.lastname,
    email: clientStore.selectedClient.email,
    phone: clientStore.selectedClient.phone,
    marketing: clientStore.selectedClient.marketing,
  };

  console.log('Customer data captured:', customerData);

  // Close the client modal
  clientStore.closeClientModal();

  // Set edit mode to create new appointment with customer data
  editStore.setEditFormId(null);
  editStore.setIsCloneMode(false);
  editStore.setIsEditMode();

  // Store customer data for the form to pick up
  eventStore.prefilledCustomer = customerData;

  console.log('Prefilled customer set:', eventStore.prefilledCustomer);
};

const cloneAppointment = (appointment) => {
  if (!clientStore.selectedClient) {
    console.error('No client selected for cloning');
    return;
  }

  // Capture customer data BEFORE closing modal
  const customerData = {
    id: clientStore.selectedClient.id,
    firstname: clientStore.selectedClient.firstname,
    lastname: clientStore.selectedClient.lastname,
    email: clientStore.selectedClient.email,
    phone: clientStore.selectedClient.phone,
    marketing: !!clientStore.selectedClient.marketing,
  };

  // Get service IDs - use service_id array directly
  let serviceIds = appointment.service_id || [];
  if (typeof serviceIds === 'string') {
    try {
      serviceIds = JSON.parse(serviceIds);
    } catch (e) {
      serviceIds = [];
    }
  }

  // Create a temp ID for the cloned appointment
  const tempId = 'temp-' + Date.now();

  // Create an appointment object with ALL appointment data for cloning
  const appointmentData = {
    customer: customerData,
    service_id: serviceIds,
    services: appointment.services || [], // Include services array for FullCalendar
    stylist: appointment.stylist,
    note: appointment.note || "",
    datetime: new Date(appointment.datetime || new Date()), // Convert to Date object
  };

  // Close the client modal AFTER capturing data
  clientStore.closeClientModal();

  // Store temp event as object property (not in array) for EditForm to access
  eventStore.events[tempId] = appointmentData;

  // Open form in clone mode
  editStore.setEditFormId(tempId);
  editStore.setIsCloneMode(true);
  editStore.setIsEditMode();
};

const showAddNoteForm = () => {
  // Access the NotesTab component and trigger its showAddNote
  if (notesTabRef.value) {
    notesTabRef.value.triggerAddNote();
  }
};

// Watch for when the modal opens to load initial data
watch(
  () => clientStore.showClientModal,
  (newVal) => {
    if (newVal) {
      // Keep search input in sync with current filter so user can clear it
      searchInput.value = clientStore.searchQuery || "";
      activeTab.value = "details";
    }
  }
);
</script>

<style scoped>
:deep(.fixed-dialog-height) {
  height: 85vh;
  min-height: 85vh;
  max-height: 85vh;
  display: flex;
}
.client-item {
  cursor: pointer;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s;
}

.client-item:hover {
  background-color: #f5f5f5;
  border-color: #1976d2;
}

.client-list-view,
.client-detail-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.client-list {
  flex: 1;
  overflow-y: auto;
}

.pagination-bar {
  border-top: 1px solid #e0e0e0;
}

/* Remove focus ring on search field */
:deep(.v-text-field .v-field) {
  box-shadow: none !important;
  outline: none !important;
}

:deep(.v-text-field .v-field--focused) {
  box-shadow: none !important;
  outline: none !important;
}

:deep(.v-text-field .v-field__outline) {
  --v-field-border-width: 1px;
  box-shadow: none !important;
}

:deep(.v-text-field .v-field--focused .v-field__outline) {
  --v-field-border-width: 1px;
  --v-field-border-opacity: 1;
  box-shadow: none !important;
}

:deep(.v-text-field .v-field--variant-outlined.v-field--focused .v-field__outline) {
  --v-field-border-width: 1px !important;
}

:deep(.v-text-field input) {
  outline: none !important;
  box-shadow: none !important;
}

:deep(.v-text-field input:focus) {
  outline: none !important;
  box-shadow: none !important;
}

/* Scrollable tabs container */
.tab-content-container {
  height: 100%;
  overflow: hidden;
}

:deep(.tab-scrollable) {
  height: calc(85vh - 200px); /* Subtract header, button bar, and tabs height */
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
